import classNames from "classnames";
import {aspectRatio, backgroundColour, backgroundPattern} from "../../utils/block-utils";
import Block from "../block";
import {loadStripe} from '@stripe/stripe-js';
import {CardElement, Elements, useElements, useStripe} from '@stripe/react-stripe-js';
import {Image} from "../../components/image";
import Currency from "@autocx/forms/src/controls/currency";
import {WebsiteContext} from "../../index";
import {useContext, useEffect, useMemo, useState} from "react";
import axios from "axios";
import AnimateHeight from "react-animate-height";
import Spinner from "../../components/spinner";
import SimpleSiteHeader from "../../components/simple-site-header";
import {InlineAccountForm} from "./index";

const handleNameSplit = (name) => {
    let firstName = '';
    let lastName = '';

    if (!name) return {firstName, lastName};

    const firstSpaceIndex = name.indexOf(' ');

    if (firstSpaceIndex === -1) {
        firstName = name;
    } else {
        firstName = name.substring(0, firstSpaceIndex);
        lastName = name.substring(firstSpaceIndex + 1);
    }

    return {firstName, lastName};
};


const CheckoutForm = ({profile, loading, setProfile, ...props}) => {
    const stripe = useStripe();
    const elements = useElements();
    const [order, setOrder] = useState(null)
    const [cardElementOptions, setCardElementOptions] = useState({})
    const [nameInvalid, setNameInvalid] = useState(false);
    const [emailInvalid, setEmailInvalid] = useState(false);
    const [email, setEmail] = useState('');
    const [fullName, setFullName] = useState('');
    const [cardComplete, setCardComplete] = useState(false);
    const [status, setStatus] = useState('');
    const [stripeError, setStripeError] = useState('');
    const [formError, setFormError] = useState('');

    const initialOrder = {
        type: props.websiteContext?.website?.defaultSaleType,
        customer: profile?.registered ? {
            emailAddress: profile.customer.emailAddress,
            firstName: profile.customer.firstName,
            lastName: profile.customer.lastName
        } : {
            emailAddress: email,
            firstName: handleNameSplit(fullName).firstName,
            lastName: handleNameSplit(fullName).lastName
        },
        products: [
            {
                productUuid: props.values?.uuid,
                quantity: 1,
            }
        ]
    }

    useEffect(() => {
        if (props.order) setOrder(props.order)
    }, [props.order])

    useEffect(() => {
        const textColour = getComputedStyle(props.pageRef.current).getPropertyValue('--text-colour');
        const font = getComputedStyle(props.pageRef.current).getPropertyValue('--base-font');
        setCardElementOptions({
            hidePostalCode: true,
            style: {
                base: {
                    color: textColour,
                    fontSize: "18px",
                    fontFamily: font,
                    fontSmoothing: "antialiased"
                }
            }
        })
    }, [props.pageRef])


    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!stripe || !elements) {
            return;
        }

        // Reset error states
        setEmailInvalid(false);
        setNameInvalid(false);
        setStripeError('');
        setFormError('');
        setStatus('');

        if (!email) {
            setEmailInvalid(true);
        }
        if (!fullName) {
            setNameInvalid(true);
        }
        if (!cardComplete) {
            setStatus('error')
            setFormError('Please enter your card details.');
        }

        // Post the order
        if ((!profile?.registered && email && fullName && cardComplete) || (profile?.customer?._links?.logout && cardComplete)) {
            try {
                let responseOrder;
                setStatus('pending')
                if (order?._links?.replaceOrderProductCollection?.href) {
                    responseOrder = await axios
                        .put(order?._links?.replaceOrderProductCollection?.href, [...initialOrder.products], {
                            timeout: 6000,
                            validateStatus: (status) => (status >= 200 && status < 300)
                        })
                        .catch((error) => {
                            if (error.code === 'ECONNABORTED') {
                                setFormError("The payment took too long to complete. Please try again.");
                            } else {
                                setFormError("There was a problem processing your payment. Your card wasn't charged. Please try again.");
                            }
                            setStatus('error');
                        });

                    responseOrder = {...order, products: [...responseOrder.data]}
                } else {
                    responseOrder = await axios
                        .post('/api/v2/orders?createPayment=true', initialOrder, {
                            timeout: 6000,
                            validateStatus: (status) => (status >= 200 && status < 300)
                        })
                        .catch((error) => {
                            if (error.code === 'ECONNABORTED') {
                                setFormError("The payment took too long to complete. Please try again.");
                            } else {
                                setFormError("There was a problem processing your payment. Your card wasn't charged. Please try again.");
                            }
                            setStatus('error');
                        });
                    responseOrder = responseOrder.data;
                }

                setOrder(responseOrder)
                const clientSecret = responseOrder.payments[0].clientSecret;

                // Stripe payment
                const card = elements.getElement(CardElement);
                const result = await stripe.confirmCardPayment(clientSecret, {
                    payment_method: {
                        card: card,
                        billing_details: {
                            email: initialOrder.customer.emailAddress,
                        },
                    }
                });

                if (result.error) {
                    setStatus('error')
                    // Handle errors in Stripe payment
                    setStripeError(result.error.message)
                    console.log(result.error.message);
                } else {
                    const currentPayment = responseOrder.payments[0];
                    axios
                        .post(currentPayment._links.edit.href, {...currentPayment, status: "Processing"}, {
                            validateStatus: (status) => (status >= 200 && status < 300)
                        })
                        .catch(() => {
                            // Maybe notify us somehow
                            console.error("Order confirmation failed.");
                        })
                        .finally(() => {
                            // Always redirect to confirmation screen because payment is successful
                            location.replace(`${location.origin}/orders/confirmation/${responseOrder.uuid}`);
                        });
                }
            } catch (err) {
                setStatus('error')
                console.log(err);
            }
        }
    };

    const handleLogout = () => {
        axios.post('/api/v2/publicusers/logout').catch((e) => console.log(e))
        setProfile(null);
    }

    return (
        <>
            {initialOrder?.type !== null ?
                <form onSubmit={handleSubmit}>
                    <AnimateHeight height={loading ? 0 : 'auto'} animateOpacity={true} className={"w-full"}
                                   contentClassName={"flex flex-col items-center gap-y-2.5"}>
                        {profile?.customer?._links?.logout ? (
                            <div className={"w-full leading-tight font-semibold"}>
                                <div>
                                    {profile?.customer?.firstName} {profile?.customer?.lastName}
                                    {profile?.customer?._links?.logout &&
                                        <span className={"text-sm text-gray-600 pl-1"}>(Not you? <a
                                            className={"cursor-pointer"}
                                            onClick={() => handleLogout()}>Log Out</a>)</span>}
                                </div>
                                <div>{profile?.customer?.emailAddress}</div>
                            </div>
                        ) : (
                            <>
                                <div className={"w-full"}>
                                    <label htmlFor="customer_email">Email</label>
                                    <input type="email" id={"customer_email"}
                                           name="customer_email"
                                           autoComplete="email"
                                           onChange={(e) => {
                                               setEmail(e.target.value)
                                               setStatus('');
                                           }}
                                           className={classNames(
                                               "w-full",
                                               emailInvalid ? "focus:ring-error-500 focus:border-error-500 border-error-300 text-error-500 focus:text-inherit" : null
                                           )}
                                           placeholder="Email"
                                           required
                                    />
                                </div>
                                <div className={"w-full"}>
                                    <label htmlFor="card_name">Name on Card</label>
                                    <input type="text" id={"card_name"}
                                           autoComplete="cc-name"
                                           name="card_name"
                                           onChange={(e) => {
                                               setFullName(e.target.value)
                                               setStatus('');
                                           }}
                                           className={classNames(
                                               "w-full",
                                               nameInvalid ? "focus:ring-error-500 focus:border-error-500 border-error-300 text-error-500 focus:text-inherit" : null
                                           )}
                                           placeholder="Name on card"
                                           autoCapitalize="words"
                                           required
                                    />
                                </div>
                            </>
                        )}

                        <div className={"w-full relative"}>
                            <label htmlFor="card_details">Card Information</label>
                            <CardElement name="card_details" options={cardElementOptions}
                                         onChange={(e) => {
                                             stripeError ? setStripeError() : null
                                             setStatus('');
                                             setCardComplete(e.complete);
                                         }}
                                         className={"!text-lg !h-[38px] !pt-[8px] w-full"}
                            />
                            <AnimateHeight height={status === "error" ? 'auto' : 0} duration={200}
                                           animateOpacity={true}>
                                {stripeError &&
                                    <div className="pt-2 font-semibold text-error-500">
                                        {stripeError}
                                    </div>
                                }
                                {status === 'error' &&
                                    <div className="pt-2 font-semibold text-error-500">
                                        {formError}
                                    </div>
                                }
                            </AnimateHeight>
                        </div>
                    </AnimateHeight>
                    <button type="submit" className={classNames("button w-full justify-center mt-6 h-[42px]",
                        status !== 'pending' ? "shimmering-background" : null)}
                            disabled={!stripe || status === 'pending'}>
                        {status === 'pending' ? <><span
                            className={"inline-block pr-4"}>Processing...</span><Spinner/></> : <>Pay Now</>}
                    </button>
                </form>
                :
                <p className="text-red-500 text-sm font-bold">
                    To start taking payments, navigate to Websites > Online Store in <a
                    href={"https://vert.ws"} target={"_blank"}>Vert</a> to set the type of
                    orders that should be created.
                </p>}
        </>
    );
};

export default function Design1(props) {
    const context = useContext(WebsiteContext)
    const imageAspectRatio = {values: {options: {imageAspectRatio: context?.website?.store?.productCardAspectRatio}}}
    const image = props.values?.images?.length ? props.values?.images[0] : null;
    const imageState = props.values?.imagesState?.length ? props.values?.imagesState[0] : null;
    const stripePromise = useMemo(() => loadStripe(props?.values?.stripeConfig?.stripePublicKey, {stripeAccount: props?.values?.stripeConfig?.stripeAccount}), [props?.values?.stripeConfig]);
    const [order, setOrder] = useState(props.values.order);
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true)
    const [showingSignIn, setShowingSignIn] = useState(false)

    useEffect(() => {
        const cancelToken = axios.CancelToken.source();
        axios
            .get('/api/v2/publicusers/profile/', {cancelToken: cancelToken.token})
            .then(({data}) => {
                if (data?.customer?._links?.orders?.href && !order) {
                    axios
                        .get(data?.customer?._links?.orders?.href)
                        .then(({data}) => {
                            data = data.sort((a, b) => new Date(b.dateCreated) - new Date(a.dateCreated));
                            const currentOrder = data.find((order) => order.finalBalance > 0)
                            if (currentOrder) setOrder(currentOrder)
                        })
                }
                setProfile(data);
            })
            .finally(() => {
                setLoading(false)
            })
            .catch(error => console.error('Error fetching user profile:', error));

        return () => cancelToken.cancel();
    }, []);

    return (
        <>
            <SimpleSiteHeader/>
            <Block {...props}
                   className={classNames(props.className, !props.values.options?.inset ? backgroundPattern(props) : null, !props.values.options?.inset ? backgroundColour(props.values.options?.backgroundColour) : null)
                   }>
                <div className={classNames("mx-auto max-w-7xl section")}>
                    <div className="flex flex-col lg:flex-row gap-x-16 gap-y-8">
                        <div className="lg:w-[55%]">
                            <h2 className="!text-2xl !mb-4">Summary</h2>
                            <div className={"flex flex-col gap-y-2"}>
                                <div
                                    className={"flex flex-row gap-x-4 gap-y-6 w-full justify-between items-center text-lg"}>
                                    <div className={"flex flex-row items-center gap-x-3"}>
                                        <div className={classNames("w-16", aspectRatio(imageAspectRatio))}>
                                            <Image
                                                alt={"Image of " + props.values.title}
                                                width={64}
                                                id={imageState?.__cx__?.id ? `image_${imageState?.__cx__?.id}` : null}
                                                src={imageState?.__cx__?.key || image}
                                                className={classNames("w-full h-full object-center object-cover rounded-theme-half")}
                                            />
                                        </div>
                                        <div>
                                            {props.values.title}
                                        </div>
                                    </div>
                                    <div>
                                        <Currency.DisplayValue
                                            currency={context.currency}
                                            value={props.values.discountedSellingPrice ? props.values.discountedSellingPrice : props.values.sellingPrice}
                                            emptyPlaceholder={""}
                                        />
                                    </div>
                                </div>
                                <div
                                    className={"flex flex-row items-center justify-between mt-3 pt-3 border-t border-gray-300"}>
                                    <div className={"font-semibold text-lg"}>Total</div>
                                    <div className={"font-semibold text-lg"}>
                                        <Currency.DisplayValue
                                            currency={context.currency}
                                            value={props.values.discountedSellingPrice ? props.values.discountedSellingPrice : props.values.sellingPrice}
                                            emptyPlaceholder={""}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        {!props?.values?.productTemplate?.hidePriceOnWebsite &&
                            <div className={"lg:w-[45%] flex flex-col"}>
                                <div className="p-4 sm:px-8 sm:pb-8 sm:pt-6 section-grey rounded-theme">
                                    <div className={"relative z-10"}>
                                        <div className={"flex flex-row items-center justify-between mb-2"}>
                                            <h3 className="!text-xl !mb-0">Checkout</h3>
                                            {!profile?.customer?._links.logout && !loading && props?.values?.stripeConfig?.stripePublicKey && props?.values?.stripeConfig?.stripeAccount &&
                                                <button
                                                    className={classNames("h-8", showingSignIn ? "font-bold text-link text-base" : "button !text-sm !px-3 !mt-0")}
                                                    onClick={(e) => {
                                                        e.preventDefault()
                                                        setShowingSignIn(prev => !prev)
                                                    }
                                                    }
                                                >{showingSignIn ? "Cancel" : "Sign In"}</button>}
                                        </div>
                                        {!props?.values?.stripeConfig?.stripePublicKey || !props?.values?.stripeConfig?.stripeAccount
                                            ? props.values.statusCode === 502
                                                ? <p className="text-red-500 text-sm font-bold">
                                                    Checking out is currently unavailable, please try again later.
                                                </p>
                                                : <p className="text-red-500 text-sm font-bold">
                                                    To start taking payments, navigate to Settings > Payments in <a
                                                    href={"https://vert.ws"} target={"_blank"}>Vert</a> to connect your
                                                    Stripe account.
                                                </p>
                                            : <>
                                                {showingSignIn
                                                    ?
                                                    <InlineAccountForm setProfile={setProfile}
                                                                       setShowingSignIn={setShowingSignIn}/>
                                                    :
                                                    <Elements stripe={stripePromise}>
                                                        <CheckoutForm profile={profile} loading={loading} {...props}
                                                                      order={order} setProfile={setProfile}/>
                                                    </Elements>
                                                }
                                            </>
                                        }
                                    </div>
                                </div>
                                <div className={"text-center"}>
                                    <a href="https://stripe.com" target="_blank" className={"mt-4 inline-block"}>
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="0 0 150 34"
                                            className={"h-[22px] fill-gray-600"}>
                                            <path
                                                d="M146 0H3.73A3.73 3.73 0 000 3.73v26.54A3.73 3.73 0 003.73 34H146a4 4 0 004-4V4a4 4 0 00-4-4zm3 30a3 3 0 01-3 3H3.73A2.74 2.74 0 011 30.27V3.73A2.74 2.74 0 013.73 1H146a3 3 0 013 3z"></path>
                                            <path
                                                d="M17.07 11.24h-4.3V22h1.92v-4.16h2.38c2.4 0 3.9-1.16 3.9-3.3s-1.5-3.3-3.9-3.3zm-.1 5h-2.28v-3.3H17c1.38 0 2.11.59 2.11 1.65s-.76 1.6-2.11 1.6zM25.1 14a3.77 3.77 0 00-3.8 4.09 3.81 3.81 0 107.59 0A3.76 3.76 0 0025.1 14zm0 6.67c-1.22 0-2-1-2-2.58s.76-2.58 2-2.58 2 1 2 2.58-.79 2.57-2 2.57z"></path>
                                            <path
                                                d="M36.78 19.35L35.37 14.13 33.89 14.13 32.49 19.35 31.07 14.13 29.22 14.13 31.59 22.01 33.15 22.01 34.59 16.85 36.03 22.01 37.59 22.01 39.96 14.13 38.18 14.13 36.78 19.35z"></path>
                                            <path
                                                d="M44 14a3.83 3.83 0 00-3.75 4.09 3.79 3.79 0 003.83 4.09A3.47 3.47 0 0047.49 20L46 19.38a1.78 1.78 0 01-1.83 1.26A2.12 2.12 0 0142 18.47h5.52v-.6C47.54 15.71 46.32 14 44 14zm-1.93 3.13A1.92 1.92 0 0144 15.5a1.56 1.56 0 011.69 1.62zM50.69 15.3v-1.17h-1.8V22h1.8v-4.13a1.89 1.89 0 012-2 4.68 4.68 0 01.66 0v-1.8h-.51a2.29 2.29 0 00-2.15 1.23zM57.48 14a3.83 3.83 0 00-3.75 4.09 3.79 3.79 0 003.83 4.09A3.47 3.47 0 0060.93 20l-1.54-.59a1.78 1.78 0 01-1.83 1.26 2.12 2.12 0 01-2.1-2.17H61v-.6c0-2.19-1.24-3.9-3.52-3.9zm-1.93 3.13a1.92 1.92 0 011.92-1.62 1.56 1.56 0 011.69 1.62zM67.56 15a2.85 2.85 0 00-2.26-1c-2.21 0-3.47 1.85-3.47 4.09s1.26 4.09 3.47 4.09a2.82 2.82 0 002.26-1V22h1.8V11.24h-1.8zm0 3.35a2 2 0 01-2 2.28c-1.31 0-2-1-2-2.52s.7-2.52 2-2.52c1.11 0 2 .81 2 2.29zM79.31 14A2.88 2.88 0 0077 15v-3.76h-1.8V22H77v-.83a2.86 2.86 0 002.27 1c2.2 0 3.46-1.86 3.46-4.09S81.51 14 79.31 14zM79 20.6a2 2 0 01-2-2.28v-.47c0-1.48.84-2.29 2-2.29 1.3 0 2 1 2 2.52s-.75 2.52-2 2.52zM86.93 19.66L85 14.13h-1.9l2.9 7.59-.3.74a1 1 0 01-1.14.79 4.12 4.12 0 01-.6 0v1.51a4.62 4.62 0 00.73.05 2.67 2.67 0 002.78-2l3.24-8.62h-1.89zM125 12.43a3 3 0 00-2.13.87l-.14-.69h-2.39v12.92l2.72-.59v-3.13a3 3 0 001.93.7c1.94 0 3.72-1.59 3.72-5.11 0-3.22-1.8-4.97-3.71-4.97zm-.65 7.63a1.61 1.61 0 01-1.28-.52v-4.11a1.64 1.64 0 011.3-.55c1 0 1.68 1.13 1.68 2.58s-.69 2.6-1.7 2.6zM133.73 12.43c-2.62 0-4.21 2.26-4.21 5.11 0 3.37 1.88 5.08 4.56 5.08a6.12 6.12 0 003-.73v-2.25a5.79 5.79 0 01-2.7.62c-1.08 0-2-.39-2.14-1.7h5.38v-1c.09-2.87-1.27-5.13-3.89-5.13zm-1.47 4.07c0-1.26.77-1.79 1.45-1.79s1.4.53 1.4 1.79zM113 13.36l-.17-.82h-2.32v9.71h2.68v-6.58a1.87 1.87 0 012.05-.58v-2.55a1.8 1.8 0 00-2.24.82zM99.46 15.46c0-.44.36-.61.93-.61a5.9 5.9 0 012.7.72v-2.63a7 7 0 00-2.7-.51c-2.21 0-3.68 1.18-3.68 3.16 0 3.1 4.14 2.6 4.14 3.93 0 .52-.44.69-1 .69a6.78 6.78 0 01-3-.9V22a7.38 7.38 0 003 .64c2.26 0 3.82-1.15 3.82-3.16-.05-3.36-4.21-2.76-4.21-4.02zM107.28 10.24l-2.65.58v8.93a2.77 2.77 0 002.82 2.87 4.16 4.16 0 001.91-.37V20c-.35.15-2.06.66-2.06-1v-4h2.06v-2.34h-2.06z"></path>
                                            <path
                                                d="M116.25 11.7L118.98 11.13 118.98 8.97 116.25 9.54 116.25 11.7z"></path>
                                            <path d="M116.25 12.61H118.98V22.25H116.25z"></path>
                                        </svg>
                                    </a>
                                </div>
                            </div>
                        }
                    </div>
                </div>
            </Block>
        </>
    )
}
